"use client";

import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/utils";
import { makeBackdropClickableOnIOS } from "../lib/ios-backdrop-click";
import { usePopupExitTeardown } from "../lib/popup-exit-teardown";

function Sheet({ actionsRef, ...props }: SheetPrimitive.Root.Props) {
  // Same full-screen backdrop, same stranding risk as Dialog — see
  // usePopupExitTeardown.
  const exitActions = usePopupExitTeardown(actionsRef);
  return <SheetPrimitive.Root actionsRef={exitActions} {...props} />;
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      // Durations mirror SheetContent so the page-dim and panel-slide finish
      // together — otherwise the overlay snaps to opaque before the panel has
      // finished sliding in (or vice versa on close).
      className={cn(
        "fixed inset-0 z-50 bg-black/50 transition-opacity ease-in-out duration-[125ms] data-starting-style:opacity-0 data-ending-style:opacity-0",
        className,
      )}
      {...props}
      ref={makeBackdropClickableOnIOS}
    />
  );
}

function SheetContent({
  className,
  children,
  side = "right",
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: "top" | "right" | "bottom" | "left";
}) {
  return (
    <SheetPortal>
      <SheetOverlay forceRender />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        className={cn(
          "bg-surface-base fixed z-50 flex flex-col gap-6 shadow-lg transition-[translate] ease-in-out duration-[125ms]",
          side === "right" &&
            "data-starting-style:translate-x-full data-ending-style:translate-x-full inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
          side === "left" &&
            "data-starting-style:-translate-x-full data-ending-style:-translate-x-full inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
          side === "top" &&
            "data-starting-style:-translate-y-full data-ending-style:-translate-y-full inset-x-0 top-0 h-auto border-b",
          side === "bottom" &&
            "data-starting-style:translate-y-full data-ending-style:translate-y-full inset-x-0 bottom-0 h-auto border-t",
          className,
          // Full-height sides underlap the status bar in the native iPad
          // shell; padding the panel by the safe-area inset keeps its content
          // (and the close button, offset below) out of that zone. 0 on
          // desktop, so nothing changes there. AFTER the caller's className:
          // tailwind-merge would otherwise let a caller's padding reset (the
          // nav drawer passes p-0) strip this inset.
          (side === "right" || side === "left") &&
            "pt-[var(--safe-area-top,0px)]",
        )}
        {...props}
      >
        {children}
        <SheetPrimitive.Close
          className={cn(
            "ring-offset-background focus:ring-ring absolute right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none",
            // Keep the close button below the iPad status bar: the panel
            // pads by --safe-area-top (0 on desktop), so this lands beside
            // the header. Bottom sheets sit mid-screen and need no inset.
            side === "bottom"
              ? "top-4"
              : "top-[calc(var(--safe-area-top,0px)_+_1rem)]",
          )}
        >
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        "relative flex flex-col gap-1.5 p-6 after:content-[''] after:block after:absolute after:bottom-0 after:left-6 after:right-6 after:h-px after:bg-border-subtle",
        className,
      )}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        "relative flex flex-col gap-2 p-6 after:content-[''] after:block after:absolute after:top-0 after:left-6 after:right-6 after:h-px after:bg-border-subtle",
        className,
      )}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
};
